import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import "./Login.css";

// Get API URL from environment variables
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

// Helper for conditional logging
const logError = (message, ...args) => {
  if (process.env.NODE_ENV === 'development') {
    console.error(message, ...args);
  }
};

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [verificationNeeded, setVerificationNeeded] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState("");
  const [isResendingVerification, setIsResendingVerification] = useState(false);
  const [resendMessage, setResendMessage] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check for verification success or password reset
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('verified') === 'true') {
      setSuccessMessage('Email verified successfully! You can now log in.');
    } else if (params.get('reset') === 'success') {
      setSuccessMessage('Password reset successful! You can now log in with your new password.');
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    setIsLoading(true);
    setVerificationNeeded(false);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/users/login`, {
        email,
        password
      });

      // Login successful
      await login(response.data.token);
      navigate("/");
      
    } catch (err) {
      logError("Login error:", err);
      
      // Check if this is a verification needed error
      if (err.response?.data?.needsVerification) {
        setVerificationNeeded(true);
        setVerificationEmail(err.response.data.email || email);
        setError('Please verify your email before logging in.');
      } else {
        setError(err.response?.data?.error || "Login failed. Please check your credentials.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!verificationEmail || isResendingVerification) return;
    
    setIsResendingVerification(true);
    setResendMessage(null);
    
    try {
      const response = await axios.post(`${API_BASE_URL}/api/users/resend-verification`, {
        email: verificationEmail
      });
      
      setResendMessage({
        type: 'success',
        text: response.data.message || 'Verification email sent! Please check your inbox.'
      });
    } catch (err) {
      logError("Resend verification error:", err);
      setResendMessage({
        type: 'error',
        text: err.response?.data?.error || 'Failed to resend verification email'
      });
    } finally {
      setIsResendingVerification(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="login">
      <h1>Login</h1>
      
      {error && <p className="error-message">{error}</p>}
      {successMessage && <p className="success-message">{successMessage}</p>}

      {verificationNeeded && (
        <div className="verification-needed">
          <p className="verification-message">
            Your email address has not been verified yet. Please check your inbox for a verification link.
          </p>
          
          {resendMessage && (
            <p className={`resend-message ${resendMessage.type}`}>
              {resendMessage.text}
            </p>
          )}
          
          <button 
            onClick={handleResendVerification}
            disabled={isResendingVerification}
            className={`resend-button ${isResendingVerification ? 'loading' : ''}`}
          >
            {isResendingVerification ? 'Sending...' : 'Resend Verification Email'}
          </button>
          
          <Link to="/verification-pending" className="verification-link">
            Need help with verification?
          </Link>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={isLoading}
        />
        
        <div className="password-field">
          <input
            type={showPassword ? "text" : "password"}
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={isLoading}
          />
          <button 
            type="button" 
            className="password-toggle" 
            onClick={togglePasswordVisibility}
            tabIndex="-1"
          >
            {showPassword ? "Hide" : "Show"}
          </button>
        </div>
        
        <div className="forgot-password-link">
          <Link to="/forgot-password">Forgot Password?</Link>
        </div>
        
        <button type="submit" disabled={isLoading} className={isLoading ? "loading" : ""}>
          {isLoading ? "Logging in..." : "Login"}
        </button>
      </form>
    </div>
  );
};

export default Login;